import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const faqCategories = [
  {
    title: 'Wertermittlung',
    items: [
      {
        question: 'Wie läuft eine Immobilienbewertung ab?',
        answer: 'Nach Ihrer Kontaktaufnahme vereinbaren wir einen Besichtigungstermin. Ich analysiere Ihre Immobilie vor Ort, prüfe den lokalen Markt und erstelle eine fundierte Werteinschätzung. Das Ergebnis besprechen wir persönlich und in Ruhe.'
      },
      {
        question: 'Was kostet eine Wertermittlung?',
        answer: 'Die Ersteinschätzung Ihrer Immobilie ist für Sie kostenlos und unverbindlich. Für ein ausführliches, schriftliches Wertgutachten erstelle ich Ihnen gerne ein individuelles Angebot.'
      },
      {
        question: 'Was ist der Unterschied zwischen Verkehrswert und Marktwert?',
        answer: 'Der Verkehrswert ist der rechtlich definierte Wert einer Immobilie gemäß § 194 BauGB. Der Marktwert beschreibt den Preis, der aktuell am Markt erzielbar ist. In der Praxis liegen beide Werte oft nah beieinander, können aber je nach Marktlage variieren.'
      },
      {
        question: 'Wie lange dauert eine Wertermittlung?',
        answer: 'Eine erste Einschätzung kann ich oft schon nach der Besichtigung geben. Für eine detaillierte, schriftliche Bewertung sollten Sie mit etwa 5-7 Werktagen rechnen.'
      },
      {
        question: 'Welche Unterlagen benötigen Sie für die Bewertung?',
        answer: 'Grundbuchauszug, Flurkarte, Baupläne/Grundrisse, Energieausweis und falls vorhanden: Wohnflächenberechnung, Nebenkostenabrechnungen und Informationen zu durchgeführten Modernisierungen.'
      }
    ]
  },
  {
    title: 'Verkauf',
    items: [
      {
        question: 'Wie lange dauert der Verkaufsprozess?',
        answer: 'Die Verkaufsdauer hängt von verschiedenen Faktoren ab: Lage, Zustand, Preis und Marktlage. Im Rhein-Main-Gebiet liegt die durchschnittliche Vermarktungszeit bei gut positionierten Immobilien bei 4-8 Wochen.'
      },
      {
        question: 'Wie diskret ist der Verkaufsprozess?',
        answer: 'Auf Wunsch vermarkte ich Ihre Immobilie vollständig diskret – ohne öffentliche Anzeigen. Ich nutze mein Netzwerk aus qualifizierten Interessenten und spreche potenzielle Käufer gezielt an.'
      },
      {
        question: 'Wie wird meine Immobilie vermarktet?',
        answer: 'Ich erstelle professionelle Exposés mit hochwertigen Fotos und vermarkte Ihre Immobilie über mein Netzwerk, ausgewählte Portale und bei passenden Interessenten aus meiner Datenbank.'
      },
      {
        question: 'Was passiert bei Besichtigungen?',
        answer: 'Ich führe alle Besichtigungen persönlich durch und qualifiziere die Interessenten vorab. Sie müssen sich um nichts kümmern – ich übernehme die gesamte Organisation und Kommunikation.'
      },
      {
        question: 'Welche Kosten entstehen beim Verkauf?',
        answer: 'Meine Provision wird erst bei erfolgreichem Verkauf fällig und ist marktüblich. Hinzu kommen für den Verkäufer ggf. Kosten für den Energieausweis und anteilige Notarkosten.'
      }
    ]
  },
  {
    title: 'Vermietung',
    items: [
      {
        question: 'Wie finden Sie den richtigen Mieter?',
        answer: 'Ich prüfe alle Interessenten sorgfältig: Bonität, Arbeitsverhältnis, Mietschuldenfreiheit und persönlicher Eindruck. So minimiere ich das Risiko von Mietausfällen.'
      },
      {
        question: 'Was ist in Ihrer Vermietungsleistung enthalten?',
        answer: 'Erstellung des Exposés, Interessentenprüfung, Besichtigungen, Mietvertragsgestaltung, Übergabeprotokoll und auf Wunsch auch die laufende Betreuung des Mietverhältnisses.'
      },
      {
        question: 'Wie schnell finden Sie einen Mieter?',
        answer: 'Im Rhein-Main-Gebiet herrscht hohe Nachfrage. Bei marktgerechter Miete finde ich in der Regel innerhalb von 2-4 Wochen einen geeigneten Mieter.'
      }
    ]
  },
  {
    title: 'Allgemeines',
    items: [
      {
        question: 'Welche Regionen betreuen Sie?',
        answer: 'Ich bin spezialisiert auf das Rhein-Main-Gebiet: Frankfurt, Wiesbaden, Darmstadt, Offenbach, Mainz, Bad Homburg, Hanau, Königstein und Umgebung.'
      },
      {
        question: 'Warum keinen großen Makler beauftragen?',
        answer: 'Bei mir haben Sie einen festen Ansprechpartner, der Ihre Immobilie persönlich kennt und betreut. Keine wechselnden Sachbearbeiter, keine Massenabfertigung – sondern individuelle Beratung auf Augenhöhe.'
      },
      {
        question: 'Wie kann ich Sie am besten erreichen?',
        answer: 'Sie erreichen mich telefonisch, per WhatsApp, E-Mail oder SMS. Ich antworte in der Regel innerhalb weniger Stunden – oft auch am Wochenende.'
      }
    ]
  }
];

export default function FAQ() {
  return (
    <div data-testid="faq-page">
      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-[#0a1120]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm mb-8">
            <Link to="/" className="text-[#c8c6c4] hover:text-[#d1af6a]">Home</Link>
            <ChevronRight className="w-4 h-4 text-[#c8c6c4]" />
            <span className="text-[#d1af6a]">FAQ</span>
          </nav>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="font-accent text-[#d1af6a] text-xs tracking-[0.2em]">
              FAQ
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white mt-4 mb-6">
              Häufig gestellte Fragen
            </h1>
            <p className="text-[#c8c6c4] text-lg leading-relaxed">
              Hier finden Sie Antworten auf die wichtigsten Fragen rund um 
              Immobilienbewertung, Verkauf und Vermietung.
            </p>
          </motion.div>
        </div>
      </section>

      {/* FAQ Categories */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
          {faqCategories.map((category, catIndex) => (
            <motion.div
              key={category.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-accent text-[#d1af6a] text-sm tracking-[0.2em] mb-6">
                {category.title.toUpperCase()}
              </h2>
              <Accordion type="single" collapsible className="space-y-4">
                {category.items.map((item, index) => (
                  <AccordionItem
                    key={index}
                    value={`${catIndex}-${index}`}
                    className="border border-[#d1af6a]/20 bg-[#1a2640] px-6"
                  >
                    <AccordionTrigger className="text-white hover:text-[#d1af6a] text-left py-5">
                      {item.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-[#c8c6c4] pb-5 leading-relaxed">
                      {item.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#1a2640]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl sm:text-3xl text-white mb-4">
            Noch Fragen?
          </h2>
          <p className="text-[#c8c6c4] mb-8">
            Kontaktieren Sie mich – ich beantworte Ihre Fragen gerne persönlich.
          </p>
          <Link to="/kontakt" className="btn-primary">
            Kontakt aufnehmen
          </Link>
        </div>
      </section>
    </div>
  );
}
